import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { ApiResources } from "../bundles/helpers.bundle";
import { IResource } from "../bundles/models.bundle";
import { AppConfig } from "../bundles/services.bundle";

@Injectable()
export class ResourceService {
    constructor(
        private readonly http: HttpClient,
        private readonly appConfig: AppConfig
    ) {
    }

    roles() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.roles));
    }

    clients() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.clients));
    }

    contractClients() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.contractClients));
    }

    programs() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.programs));
    }

    internalOwners() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.internalOwners));
    }

    clientOwners() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.clientOwners));
    }

    riskTypes() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.riskTypes));
    }

    measureWeights() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.measureWeights));
    }

    attributions() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.attributions));
    }

    payers() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.payers));
    }

    formats() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.formats));
    }

    reports() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.reports));
    }

    measures() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.measures));
    }
    dropDownMeasures(userId: number) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.dropDownMeasures), { userId: userId });
    }

    templates() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.templates));
    }

    contractTemplates() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.contractTemplates));
    }

    patientContracts(patientId: number) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.patientContracts), { patientId: patientId });
    }

    templateVersions(templateTitle: string) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.templateVersions), { templateTitle: templateTitle });
    }

    measureCodes(clientId: number, contractId: number) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.measureCodes), { clientId: clientId, contractId: contractId });
    }

    contractTemplateVersions(templateTitle: string) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.contractTemplateVersions), { templateTitle: templateTitle });
    }

    organizations(userId: number, clientId?: number) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.organizations), { userId: userId, clientId: clientId });
    }

    userOrganizations(userId: number) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.userOrganizations), { userId: userId });
    }

    practices(userId: number, clientId?: number, organizationId?: number) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.practices), { userId: userId, clientId: clientId, organizationId: organizationId });
    }

    providers(userId: number, clientId?: number, organizationId?: number, practiceId?: number) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.providers), { userId: userId, clientId: clientId, organizationId: organizationId, practiceId: practiceId });
    }

    contracts(clientId?: string | number, organizationId?: number, practiceId?: number, providerId?: number) {
        if (typeof clientId === "string") {
            return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.contractsView), { encryptedClientId: clientId });
        } else {
            return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.contracts), { clientId: clientId, organizationId: organizationId, practiceId: practiceId, providerId: providerId });
        }
    }

    dsripContracts(clientId: number) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.fetchDsripContracts), { clientId: clientId });
    }

    qipContracts(clientId: number) {
      return this.fetch(this.appConfig.getURI(ApiResources.qipReports.base, ApiResources.qipReports.fetchQipContracts), { clientId: clientId });
    }

    activeContracts(clientId: string) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.fetchActiveContracts), { encryptedClientId: clientId });
    }

    selectedPayer(templateId: number) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.selectedPayer), { templateId: templateId });
    }

    templateMeasureGroups(contractId: number) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.templateMeasureGroups), { contractId: contractId });
    }

    templateTags(clientId?: null | number, organizationId?: number, practiceId?: number, providerId?: number) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.templateTags), { clientId: clientId, organizationId: organizationId, practiceId: practiceId, providerId: providerId });
    }

    allTemplateTags(clientId?: number) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.allTemplateTags), { clientId: clientId });
    }

    fetchUserBasedOnClientId(clientId?: number) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.fetchUserByClient), { clientId: clientId });
    }

    mipsClients() {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.fetchMipsClient));
  }

  qipClients() {
    return this.fetch(this.appConfig.getURI(ApiResources.qipReports.base, ApiResources.qipReports.fetchQipClient));
    }

    fetchMeasuresBasedOnFormat(format: string,clientId?: number) {
        return this.fetch(this.appConfig.getURI(ApiResources.resources.base, ApiResources.resources.fetchMeasuresBasedOnFormat),
            {clientId: clientId });
  }

  fetchQipMeasures(clientId?: number) {
    return this.fetch(this.appConfig.getURI(ApiResources.qipReports.base, ApiResources.qipReports.fetchQipMeasures),
      {  clientId: clientId });
  }

    private fetch(apiEndPoint: string, request?: any): Observable<Array<IResource>> {
        let headers = new HttpHeaders();
        headers = headers.append("Auth", "True");
        return this.http.post<Array<IResource>>(apiEndPoint, request, { headers: headers });
    }
}
